import React, { useRef, useEffect } from 'react';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { isDarkAtom } from '../../../../atoms/shared/uiAtoms';
import { 
  projectV2ShowCompletedTasksAtom,
  projectV2ShowCompletedProjectsAtom 
} from '../../../../atoms/v3/projectV2Atoms';

interface CompletionControlPanelProps {
  isOpen: boolean;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLElement>;
  showTasksToggle?: boolean; // 是否显示已完成任务开关
}

/**
 * 完成状态控制面板
 * 包含已完成项目和已完成任务的独立开关
 */
export const CompletionControlPanel: React.FC<CompletionControlPanelProps> = ({
  isOpen,
  onClose,
  anchorRef,
  showTasksToggle = true
}) => {
  const { t } = useTranslation(['v3/projects']);
  const [isDark] = useAtom(isDarkAtom);
  const [showCompletedProjects, setShowCompletedProjects] = useAtom(projectV2ShowCompletedProjectsAtom);
  const [showCompletedTasks, setShowCompletedTasks] = useAtom(projectV2ShowCompletedTasksAtom);
  const panelRef = useRef<HTMLDivElement>(null);

  // 点击外部关闭面板
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      
      // 检查点击是否在面板内部或锚点元素内部
      if (
        panelRef.current && !panelRef.current.contains(target) &&
        anchorRef.current && !anchorRef.current.contains(target)
      ) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose, anchorRef]);

  // ESC 键关闭面板
  useEffect(() => {
    if (!isOpen) return;

    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => document.removeEventListener('keydown', handleEscKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      ref={panelRef}
      className={`
        absolute top-full right-0 mt-2 w-56 rounded-lg shadow-lg border z-50
        ${isDark 
          ? 'bg-ios-dark-gray6 border-ios-dark-gray4' 
          : 'bg-white border-gray-200'
        }
      `}
    >
      {/* 面板内容 */}
      <div className="p-3 space-y-3">
        {/* 标题 */}
        <div className={`text-sm font-medium ${
          isDark ? 'text-ios-dark-gray1' : 'text-gray-900'
        }`}>
          显示选项
        </div>

        {/* 已完成项目开关 */}
        <div className="flex items-center justify-between">
          <span className={`text-sm ${
            isDark ? 'text-ios-dark-gray2' : 'text-gray-700'
          }`}>
            已完成项目
          </span>
          <button
            onClick={() => setShowCompletedProjects(!showCompletedProjects)}
            className={`
              relative inline-flex h-5 w-9 items-center rounded-full transition-colors
              ${showCompletedProjects
                ? 'bg-blue-600'
                : isDark 
                  ? 'bg-ios-dark-gray4' 
                  : 'bg-gray-300'
              }
            `}
          >
            <span
              className={`
                inline-block h-3 w-3 transform rounded-full bg-white transition-transform
                ${showCompletedProjects ? 'translate-x-5' : 'translate-x-1'}
              `}
            />
          </button>
        </div>

        {/* 已完成任务开关 - 只在特定模式下显示 */}
        {showTasksToggle && (
          <div className="flex items-center justify-between">
            <span className={`text-sm ${
              isDark ? 'text-ios-dark-gray2' : 'text-gray-700'
            }`}>
              已完成任务
            </span>
            <button
              onClick={() => setShowCompletedTasks(!showCompletedTasks)}
              className={`
                relative inline-flex h-5 w-9 items-center rounded-full transition-colors
                ${showCompletedTasks
                  ? 'bg-blue-600'
                  : isDark
                    ? 'bg-ios-dark-gray4'
                    : 'bg-gray-300'
                }
              `}
            >
              <span
                className={`
                  inline-block h-3 w-3 transform rounded-full bg-white transition-transform
                  ${showCompletedTasks ? 'translate-x-5' : 'translate-x-1'}
                `}
              />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
